import React from "react";
import { Pencil } from "lucide-react";
import { Address, coverage } from "./StepAddresses";

const Row = ({ k, v }: { k: string; v?: string }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2.5 text-[15px] last:border-0">
    <span className="text-[#64748B]">{k}</span>
    <span className="text-right font-medium text-[#1A2536]">{v || "—"}</span>
  </div>
);

function Section({ title, onEdit, testId, children }: { title: string; onEdit: () => void; testId: string; children: React.ReactNode }) {
  return (
    <div className="card-soft p-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-bold">{title}</h3>
        <button type="button" onClick={onEdit} data-testid={testId} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-[#1B365D] hover:bg-[#F1EFEA]">
          <Pencil className="h-4 w-4" /> Edit
        </button>
      </div>
      {children}
    </div>
  );
}

export default function StepReview({ data, goTo, dow }: { data: any; goTo: (s: number) => void; dow?: boolean }) {
  const o = 1;
  const p = data.personal || {};
  const addrs: Address[] = data.addresses || [];
  const cov = coverage(addrs);
  return (
    <div className="space-y-5" data-testid="wizard-review">
      <Section title="About you" onEdit={() => goTo(0 + o)} testId="review-edit-personal">
        <Row k="Name" v={[p.first_name, p.middle_name, p.last_name].filter(Boolean).join(" ")} />
        <Row k="Last name at birth" v={p.birth_same === "yes" ? "Same as current" : p.birth_last_name} />
        <Row k="Other names" v={p.other_names} />
        <Row k="Marriages" v={p.spouse} />
        <Row k="Date of birth" v={p.dob} />
        <Row k="SSN" v={p.ssn} />
        <Row k="Email" v={p.email} />
        <Row k="Phone" v={p.phone} />
      </Section>
      <Section title={`Addresses (${addrs.length})`} onEdit={() => goTo(1 + o)} testId="review-edit-addresses">
        {addrs.map((a, i) => (
          <Row key={i} k={`${a.from} → ${a.to || "now"}`} v={`${a.street}, ${a.city}${a.country === "United States" ? `, ${a.state} ${a.zip}` : `, ${a.country === "Other" ? a.other_country : a.country}`}`} />
        ))}
        <p className="mt-3 text-sm text-[#64748B]">States to be searched: {cov.states.join(", ") || "—"}{cov.foreign && " · Includes an address outside the U.S. (manual review)"}</p>
      </Section>
      {dow && (
        <Section title="Court & ministry" onEdit={() => goTo(3)} testId="review-edit-ministry">
          <Row k="Organization" v={data.dow?.location} />
          <Row k="Volunteer or Employee" v={data.dow?.role} />
          <Row k="Position" v={data.dow?.position} />
        </Section>
      )}
      <Section title="Consent & signature" onEdit={() => goTo(dow ? 4 : 3)} testId="review-edit-consent">
        <Row k="Authorization" v={data.consent?.signature_name ? "Agreed" : "Not yet"} />
        <Row k="Signed as" v={data.consent?.signature_name} />
      </Section>
    </div>
  );
}
